import { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { Time_Entries } from "@/src/gql/graphql";

// Import detail actions from TimeEntryDetailConfig
import { TIME_ENTRY_DETAIL_CONFIG } from "./TimeEntryDetailConfig";

// Import saved views and quick filters
import { TIME_ENTRY_SAVED_VIEWS } from "../config/saved-views";
import { TIME_ENTRY_QUICK_FILTERS } from "@/components/shared/config/quick-filters/domain/time-entry-filters";

// Import field configurations
import { TIME_ENTRY_FIELD_CONFIGS } from "../config/field-configs";

/** Logical order for create time entry form: template → when/what → ticket → description/hours/type → times */
const CREATE_TIME_ENTRY_FIELD_ORDER: string[] = [
  "template",
  "date_worked",
  "ticket_id",
  "description",
  "hours",
  "entry_type",
  "start_time",
  "end_time",
];

/**
 * Progressive-disclosure section layout for the Create Time Entry form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_TIME_ENTRY_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["template", "date_worked", "hours", "description"],
  },
  {
    id: "relationships",
    kind: "collapsible",
    summary: "Link to a ticket",
    fieldNames: ["ticket_id"],
  },
  {
    id: "classification",
    kind: "collapsible",
    summary: "Entry type",
    fieldNames: ["entry_type"],
  },
  {
    id: "scheduling",
    kind: "collapsible",
    title: "Start/end times",
    summary: "Optional start and end times",
    fieldNames: ["start_time", "end_time"],
  },
];

/**
 * Unified Time Entry Configuration
 * This single configuration serves both table display and form creation/editing
 * Eliminates duplication between multiple time entry config files
 */
export const TIME_ENTRY_CONFIG: SimpleTableConfig<Time_Entries> = {
  entity: "time_entries",
  displayName: "Time Entry",
  statusFieldId: "sync_status",
  defaultPageSize: 10,
  defaultSort: { field: "date_worked", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,
  autoIncludeUserId: true, // Auto-populate user_id with current user

  createFormFieldOrder: CREATE_TIME_ENTRY_FIELD_ORDER,
  createFormSections: CREATE_TIME_ENTRY_FORM_SECTIONS,

  /** Default table column order: when → who → where (client/ticket) → how long → type → billing */
  tableColumnOrder: [
    "date_worked",
    "user_id",
    "company_id",
    "ticket_id",
    "hours",
    "entry_type",
    "is_approved",
    "billable_rate",
  ],

  /** Card view: what work → when → how long → where (ticket/client) */
  cardFaceFieldOrder: [
    "description",
    "date_worked",
    "hours",
    "ticket_id",
    "company_id",
    "entry_type",
  ],

  mobileDetailSections: [
    { id: "work", label: "Work" },
    { id: "context", label: "Ticket & Company" },
    { id: "billing", label: "Billing" },
    { id: "sync", label: "Sync & QuickBooks" },
    { id: "approval", label: "Approval" },
    { id: "metadata", label: "Metadata" },
  ],

  fields: TIME_ENTRY_FIELD_CONFIGS,

  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },

  // Add detail page actions for table rows
  detailActions: TIME_ENTRY_DETAIL_CONFIG.actions,

  features: {
    search: true,
    filters: true,
    export: true,
    bulkActions: true,
    columnResizing: true,
    columnVisibility: true,
    savedViews: true,
    infiniteScroll: false,
    multiColumnSort: true,
    calendarView: true,
  },

  savedViews: TIME_ENTRY_SAVED_VIEWS,

  // Quick filters for table filtering
  quickFilters: TIME_ENTRY_QUICK_FILTERS,

  // Custom filter hooks for saved views (can be populated later if needed)
  customFilterHooks: {},
};
